import { AttachButton } from '@/components/common/button/AttachButton';
import { AttachFileTableDTO } from '@/types/attachment/AttachFileTableDTO';
import { AttachmentStatus } from '@/types/attachment/AttachmentStatus';
import { EnclosureDTO } from '@/types/enclosure/EnclosureDTO';
import { EnclosureIcon } from '@/app/icons/enclosure/EnclosureIcon';
import { EnclosureType } from '@/types/enclosure/EnclosureType';
import { Grid } from '@mui/material';
import { LetterTableDTO } from '@/types/letter/LetterTableDTO';
import { Theme } from '@mui/material/styles';
import React, { FC, MouseEvent } from 'react';

interface Props {
  letter: LetterTableDTO;
  /** The photo whose full-size bytes are being loaded, so only the row holding it spins. */
  photoId?: number;
  isAttachFileLoading?: boolean;
  onAttachClick: (letter: LetterTableDTO, attachFile: AttachFileTableDTO) => void;
}

export const AttachmentRow: FC<Props> = (props: Props) => {
  const { letter, photoId, isAttachFileLoading } = props;

  const handleAttachClick = (letter: LetterTableDTO, attachFile: AttachFileTableDTO): void => {
    const { onAttachClick } = props;
    if (letter) {
      onAttachClick(letter, attachFile);
    }
  };

  const photos: AttachFileTableDTO[] = letter?.attachFiles || [];

  const declinedCount: number = photos.filter(
    (attachFile: AttachFileTableDTO): boolean => attachFile.status === AttachmentStatus.DECLINED,
  ).length;

  const severity: 'warning' | 'error' | undefined =
    declinedCount === 0 ? undefined : declinedCount === photos.length ? 'error' : 'warning';

  const isPhotoLoading: boolean =
    !!isAttachFileLoading &&
    photos.some((attachFile: AttachFileTableDTO): boolean => Number(photoId) === Number(attachFile.id));

  const sudokuEnclosures: EnclosureDTO[] = (letter?.enclosures || []).filter((enclosure: EnclosureDTO): boolean => {
    return enclosure.type === EnclosureType.SUDOKU;
  });

  const crosswordEnclosures: EnclosureDTO[] = (letter?.enclosures || []).filter((enclosure: EnclosureDTO): boolean => {
    return enclosure.type === EnclosureType.CROSSWORD;
  });

  const newsEnclosures: EnclosureDTO[] = (letter?.enclosures || []).filter((enclosure: EnclosureDTO): boolean => {
    return enclosure.type === EnclosureType.NEWS || enclosure.type === EnclosureType.SPORTS_NEWS;
  });

  return (
    <Grid container={true} direction={'row'} alignItems={'center'} wrap={'nowrap'} spacing={1}>
      {photos.length > 0 && (
        <Grid item={true}>
          <AttachButton
            aria-label={photos.length === 1 ? 'Фотография письма' : 'Фотографии письма'}
            severity={severity}
            isLoading={isPhotoLoading}
            disabled={isAttachFileLoading}
            onClick={(e: MouseEvent<HTMLButtonElement>): void => {
              e.stopPropagation();
              handleAttachClick(letter, photos[0]);
            }}
          />
        </Grid>
      )}
      {(sudokuEnclosures || []).length > 0 && (
        <Grid item={true}>
          <EnclosureIcon
            type={EnclosureType.SUDOKU}
            sx={{
              display: 'flex',
              height: (theme: Theme) => theme.spacing(3),
              color: (theme: Theme) => theme.colors.iconColor,
            }}
          />
        </Grid>
      )}
      {(crosswordEnclosures || []).length > 0 && (
        <Grid item={true}>
          <EnclosureIcon
            type={EnclosureType.CROSSWORD}
            sx={{
              display: 'flex',
              height: (theme: Theme) => theme.spacing(3),
              color: (theme: Theme) => theme.colors.iconColor,
            }}
          />
        </Grid>
      )}
      {(newsEnclosures || []).length > 0 && (
        <Grid item={true}>
          <EnclosureIcon
            type={EnclosureType.NEWS}
            sx={{
              display: 'flex',
              height: (theme: Theme) => theme.spacing(3),
              color: (theme: Theme) => theme.colors.iconColor,
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};
